/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable no-empty */
/* eslint-disable @next/next/no-img-element */
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTheme } from 'styled-components';

import customAxios from '~/common/services/customAxios';
import { useAuth } from '~/common/providers/auth';
import { IAugustoPost } from '~/common/interfaces/IAugustoPost';
import { vipUsers } from '~/common/constants/vip-users';
import { Typography } from '~/common/components/Typography';
import { Icon } from '~/common/components/Icons';
import { Tooltip } from '~/common/components/Above/Tooltip';
import { Above } from '~/common/components/Above';

import {
  AugustoInsightsContainer,
  ClosePreviewWrapper,
  PostPreview,
  PostsCount,
} from './styles';
import { AugustoPost } from './Post';
import { AugustoInsightsModal } from './Modal';
import { usePrivateLayout } from '../../providers/privateLayout';
import augusto from '../../../../../public/images/augusto.jpeg';

interface IFloatingDraggableAugustoButtonProps {
  highlight: boolean;
  pendingPosts: number;
  onClick: () => void;
}
const FloatingDraggableAugustoButton: FC<
  IFloatingDraggableAugustoButtonProps
> = ({ highlight, pendingPosts, onClick }) => {
  const [offset, setOffset] = useState(0);
  const startX = useRef<number | null>(null);
  const isDragging = useRef(false);
  const currentOffset = useRef(0);
  const maxOffset = 160; // limite para a esquerda
  const dragThreshold = 5; // px para considerar que é arraste

  const handleMouseMove = (e: MouseEvent): void => {
    if (startX.current !== null) {
      const delta = startX.current - e.clientX; // arrastando para esquerda → positivo
      if (Math.abs(delta) > dragThreshold) {
        isDragging.current = true;
      }
      const newOffset = Math.min(
        Math.max(0, currentOffset.current + delta),
        maxOffset
      );
      setOffset(newOffset);
    }
  };

  const handleMouseUp = (): void => {
    currentOffset.current = offset;
    startX.current = null;
    window.removeEventListener('mousemove', handleMouseMove);
    window.removeEventListener('mouseup', handleMouseUp);
  };

  const handleMouseDown = (e: React.MouseEvent): void => {
    startX.current = e.clientX;
    isDragging.current = false;
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  const handleTouchMove = (e: TouchEvent): void => {
    if (startX.current !== null) {
      const delta = startX.current - e.touches[0].clientX;
      const newOffset = Math.min(
        Math.max(0, currentOffset.current + delta),
        maxOffset
      );
      setOffset(newOffset);
    }
  };

  const handleTouchEnd = (): void => {
    currentOffset.current = offset;
    startX.current = null;
    window.removeEventListener('touchmove', handleTouchMove);
    window.removeEventListener('touchend', handleTouchEnd);
  };

  // suporte a touch (mobile)
  const handleTouchStart = (e: React.TouchEvent): void => {
    startX.current = e.touches[0].clientX;
    window.addEventListener('touchmove', handleTouchMove);
    window.addEventListener('touchend', handleTouchEnd);
  };

  const handleClick = (): void => {
    if (!isDragging.current) {
      onClick();
    }
  };

  return (
    <AugustoInsightsContainer
      highlight={highlight}
      offset={offset}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      onClick={handleClick}
    >
      <div style={{ position: 'relative' }}>
        <Tooltip.Simple message="Segure e arraste para reposicionar">
          <img
            src={augusto.src}
            alt=""
            style={{ width: '100%', height: '100%' }}
            draggable={false}
          />
        </Tooltip.Simple>
        <PostsCount visible={pendingPosts > 0}>
          <Typography.Paragraph size="x-small" color="neutral.0">
            {pendingPosts || 0}
          </Typography.Paragraph>
        </PostsCount>
      </div>
    </AugustoInsightsContainer>
  );
};

export const AugustoInsights: FC = () => {
  const theme = useTheme();
  const { callSubscriptionUpgradeModal } = usePrivateLayout();
  const {
    user: { subscription: userType },
  } = useAuth();

  const [reloadIndex, setReloadIndex] = useState<number>(0);
  const [preview, setPreview] = useState<IAugustoPost | null>(null);
  const [augustoPosts, setAugustoPosts] = useState<IAugustoPost[]>([]);
  const [highlight, setHighlight] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const handleLoadPosts = useCallback(async () => {
    try {
      if (!userType || !vipUsers.includes(userType)) {
        return;
      }

      const startFromId = augustoPosts.length
        ? Math.max(...augustoPosts.map((item) => Number(item.id)))
        : null;

      const { data: response } = await customAxios.get<IAugustoPost[]>(
        '/Backes/posts',
        {
          params: {
            startFromId: Number(startFromId),
          },
        }
      );

      if ((response || []).length) {
        setHighlight(response.some((post) => !post.isRead));
        setAugustoPosts((prev) => [
          ...response.filter((where) => !prev.some((p) => p.id === where.id)),
          ...prev,
        ]);

        if (response.some((post) => !post.isRead)) {
          const [toPreview] = response.filter((post) => !post.isRead);
          setPreview(toPreview);
        }
      }
    } catch {}
  }, [augustoPosts, userType]);

  const updatePost = useCallback((postId: number, post: IAugustoPost) => {
    setAugustoPosts((prev) => prev.map((p) => (p.id === postId ? post : p)));
  }, []);

  const filterPost = useCallback((postId: number) => {
    setAugustoPosts((prev) => prev.filter((p) => p.id !== postId));
  }, []);

  const handleOpenModal = useCallback(() => {
    if (!userType || !vipUsers.includes(userType)) {
      callSubscriptionUpgradeModal('platinum');
      return;
    }

    setAugustoPosts((prev) =>
      prev.map((post) => ({
        ...post,
        isRead: true,
      }))
    );

    const postIds = augustoPosts.map((post) => post.id);
    customAxios
      .post('/Backes/posts/mark-as-read', {
        postIds,
      })
      .catch(() => {});

    setHighlight(false);
    setModalOpen(true);
  }, [augustoPosts, callSubscriptionUpgradeModal, userType]);

  const pendingPosts = useMemo(
    () => augustoPosts.filter((post) => !post.isRead).length || 0,
    [augustoPosts]
  );

  useEffect(() => {
    handleLoadPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userType, reloadIndex]);

  useEffect(() => {
    let time = 0;
    setInterval(() => {
      time += 1000;

      if (time >= 20000) {
        setReloadIndex((prev) => prev + 1);
        time = 0;
      }
    }, 1000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (preview) {
      setTimeout(() => {
        setPreview(null);
      }, 10000);
    }
  }, [preview]);

  return (
    <>
      <AugustoInsightsModal
        open={modalOpen}
        close={() => setModalOpen(false)}
        posts={augustoPosts}
        updatePost={updatePost}
        filterPost={filterPost}
        reload={handleLoadPosts}
      />

      <PostPreview visible={!!preview}>
        {preview && (
          <div>
            <ClosePreviewWrapper>
              <Above.Tooltip.Simple message="Fechar pré-visualização">
                <Icon
                  type="close-line"
                  size={20}
                  onClick={() => setPreview(null)}
                  color={theme.neutral[0]}
                />
              </Above.Tooltip.Simple>
            </ClosePreviewWrapper>

            <div id="preview-content">
              <AugustoPost onlyContent post={preview} />
            </div>
          </div>
        )}
      </PostPreview>

      <FloatingDraggableAugustoButton
        highlight={highlight}
        pendingPosts={pendingPosts}
        onClick={handleOpenModal}
      />
    </>
  );
};
